/**
 * BALANCE — every tunable number in the game, in one file.
 *
 * These are design decisions, not implementation details. Anything that could
 * reasonably be argued about in a playtest lives here so it can be argued about
 * in one place.
 */

export const BALANCE = {
  round: {
    durationSec: 8 * 60,
    briefingSec: 15,
    /** The clock turns red and the music changes here. */
    warnAtSec: 60,
  },

  carry: {
    /**
     * Hard ceiling in kilograms. You physically cannot pick up past this — the
     * prompt reads "TOO HEAVY". Set so a full suit of medieval armour (28kg)
     * plus one good haul is possible but stupid.
     */
    capacityKg: 45,
    /** Loot and consumables share these; tools have their own two slots. */
    itemSlots: 8,
    toolSlots: 2,
    /**
     * Encumbrance curve. Speed is interpolated linearly between tier
     * thresholds, so there is no cliff you can stand exactly on top of — but
     * the tier names give the HUD something honest to say.
     */
    tiers: [
      { name: 'unburdened', kg: 0, speed: 1.0 },
      { name: 'loaded', kg: 10, speed: 0.92 },
      { name: 'heavy', kg: 22, speed: 0.78 },
      { name: 'staggering', kg: 34, speed: 0.6 },
      { name: 'immobile', kg: 45, speed: 0.42 },
    ],
    /** Jump height multiplier at full capacity. Interpolated the same way. */
    jumpAtCapacity: 0.45,
    /** Dropping everything you carry takes this long, and makes noise. */
    dumpSec: 0.8,
  },

  dropoff: {
    /**
     * SOLO CHUTE — a pneumatic tube in a service alcove. One thief, no waiting,
     * but the mall takes its cut. This is the safe, boring, always-available
     * option, and it should feel like settling.
     */
    solo: {
      payoutRate: 0.65,
      secPerItem: 0.45,
      /** Audible radius while the tube is running. */
      noise: 14,
    },
    /**
     * LOADING DOCK — the roller door only lifts with two thieves on it, one
     * holding the release. Full rate, plus a bonus, plus a cut for whoever
     * helped. Coordinating is the highest-yield thing in the game, and it is
     * also the loudest and the most exposed.
     */
    dock: {
      payoutRate: 1.0,
      minThieves: 2,
      /** Extra fraction on top of full rate for using the dock at all. */
      teamBonus: 0.15,
      /** Each assisting thief earns this fraction of the haul, from nowhere. */
      assistShare: 0.1,
      secPerItem: 0.3,
      noise: 26,
      /** How close an assisting thief must be to count, in metres. */
      assistRadius: 4,
    },
    /**
     * Walking out of the mall with loot still on you banks it at a penalty. It
     * exists so a thief who cannot reach a drop-off is not simply griefed, and
     * so the last thirty seconds have a real decision in them.
     */
    extraction: { payoutRate: 0.45 },
  },

  capture: {
    /** Guard reach for a tackle, in metres. */
    reachM: 2.2,
    /**
     * A caught thief drops everything they were carrying on the spot. The loot
     * stays lootable — a good crew can go back for a friend's haul, which is
     * the most interesting thing that can happen in a round.
     */
    dropsLootOnCapture: true,
    /** Guards get back up. Thieves do not. */
    guardDownSec: 12,
  },

  /**
   * THE GUARDS.
   *
   * The whole design of this section is one decision: a guard must be slower
   * than a sprinting thief and faster than a walking one. That single inequality
   * is what makes the weight system matter — an unburdened thief can outrun a
   * guard for the four and a half seconds their stamina lasts, which is just
   * long enough to get round a corner, and a thief carrying a suit of armour
   * cannot outrun one at all. Everything else here is in service of making the
   * moment you are noticed legible enough to react to.
   */
  guard: {
    /** Ambling a patrol route. Deliberately slower than a thief's walk. */
    patrolSpeed: 2.4,
    /** Walking to a camera contact or a noise. Purposeful, not yet running. */
    investigateSpeed: 4.0,
    /**
     * Full pursuit. Below the thief's 6.1 sprint and above their 3.4 walk, so
     * escaping is a real option and staying loaded is a real risk.
     */
    chaseSpeed: 5.2,

    /** Vision cone, full angle in degrees, and how far it reaches. */
    viewDeg: 104,
    viewRangeM: 26,
    /**
     * Inside this, the cone stops mattering — someone brushing past your
     * shoulder gets noticed whether or not you were looking at them.
     */
    pointBlankM: 2.6,

    /**
     * Awareness gained per second at point-blank range, before modifiers. One
     * over this is roughly the number of seconds you have to get out of sight.
     */
    noticeRate: 1.3,
    /** Crouching and moving slowly. Sneaking has to be worth doing. */
    crouchMul: 0.42,
    /** Standing still. A stationary silhouette is genuinely hard to pick out. */
    stillMul: 0.55,
    /** Sprinting through a guard's cone is the loudest thing you can do. */
    sprintMul: 1.9,
    /** Caught in the torch beam rather than merely in the cone. */
    beamMul: 1.7,

    /**
     * STANDING IN THE DARK.
     *
     * Multiplier on the notice rate at zero exposure, ramping to 1.0 at
     * `fullyLitAt`. This is the largest single modifier in the table on
     * purpose: it is the only one the player can read off the world itself
     * rather than off the HUD, and the building is lit as a set of pools with
     * a great deal of dark between them precisely so that "which way do I walk
     * across this concourse" is a real question.
     *
     * 0.3 rather than 0 because an unlit thief is still a silhouette against a
     * moonlit floor. Shadow is a discount on being seen, never a cloak — a
     * guard who walks into you sees you whatever the lighting is doing, which
     * the point-blank floor further down enforces.
     */
    shadowMul: 0.3,
    /**
     * Exposure at which the discount is entirely gone. Below 1.0 so that a
     * half-lit spot is already dangerous: the interesting decision is whether
     * to cross a pool of light, and that decision gets blunted if the edges of
     * every pool are safe.
     */
    fullyLitAt: 0.72,

    /** Awareness bled off per second once sight is broken. */
    forgetRate: 0.3,

    /** Above this the guard stops, turns, and stares. The warning shot. */
    suspectAt: 0.34,
    /** Below this a pursuing guard gives up the chase and starts sweeping. */
    chaseHoldAt: 0.42,

    /** Seconds spent looking around at each patrol waypoint. */
    dwellSec: 3.0,
    /** Seconds a guard will sweep around a last-known position before leaving. */
    searchSec: 16,
    /** Radius of that sweep, in metres. */
    searchRadiusM: 6.5,

    /**
     * Tackle range. Slightly inside the sim's capture reach so the grab lands
     * a moment before the rule says it may, rather than a moment after.
     */
    grabM: 1.8,
    /** Torch cone, full angle in degrees, and its reach. */
    torchDeg: 46,
    torchRangeM: 16,
  },

  security: {
    /** Camera cone half-angle in degrees and range in metres. */
    cameraFovDeg: 32,
    cameraRangeM: 22,
    /** Seconds of continuous sight before a camera reports a confirmed contact. */
    cameraDwellSec: 0.8,
    /** How long a contact stays on the guard's map after the thief breaks sight. */
    contactMemorySec: 6,
    /** Chute travel time between control room and a mall drop point. */
    chuteTravelSec: 3.5,
    /** Chute cooldown, so the guard cannot ping-pong across the mall. */
    chuteCooldownSec: 25,
  },

  noise: {
    /** Audible radius in metres, by action. Guards see these as map pings. */
    walk: 0,
    run: 9,
    jumpLand: 7,
    glassBreak: 34,
    shutterForce: 20,
    /** Multiplier applied while a silence consumable is active. */
    silencedMul: 0.25,
  },

  move: {
    walkSpeed: 3.4,
    runSpeed: 6.1,
    crouchSpeed: 1.7,
    jumpSpeed: 4.6,
    /** Metres the controller will step up without a jump. */
    stepHeight: 0.42,
    /** Player capsule. */
    radius: 0.34,
    standHeight: 1.78,
    crouchHeight: 1.05,
    eyeOffset: -0.14,
    gravity: -20.0,
    /** Ground acceleration and air control, in units of target speed per second. */
    accelGround: 12,
    accelAir: 2.6,
    /** Sprint drains this per second; standing still refills at half the rate. */
    staminaDrain: 0.22,
    staminaRegen: 0.14,
  },
} as const;

export type EncumbranceTier = (typeof BALANCE.carry.tiers)[number]['name'];
